import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  COMMERCIAL_LEASE_MUNICIPAL_LESSEE_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_LESSEE_CONTACT_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_EQUIPMENT_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_EQUIPMENT_DETAIL_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_TERMS_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_ESSENTIAL_USE_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_PRIMARY_BANK_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_FINANCIAL_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_LAW_FIRM_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_INSURANCE_INFO,
  COMMERCIAL_LEASE_MUNICIPAL_SSN,
  COMMERCIAL_LEASE_MUNICIPAL_FINAL,
} from 'app/models/sections/constants';
import { getCommercialLeaseMunicipalQuestionsByName } from 'app/models/questions/commercial-lease-municipal';
import { ConfirmationCommercial } from 'app/components/ConfirmationCommercial';
import { handleCommercialFormSubmit } from 'app/actions/form/actions';
import { CollectLeaseContactSsnStep } from 'app/components/CollectLeaseContactSsnStep';

export const getCommercialLeaseMunicipalSectionById = (id: number): Section => {
  return COMMERCIAL_LEASE_MUNICIPAL_SECTIONS.find((section) => section.id === id) as Section;
};

const COMMERCIAL_LEASE_MUNICIPAL_SECTIONS: Section[] = [
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_LESSEE_INFO,
    title    : 'Tell us about the lessee',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialBusinessStartDate,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_LESSEE_CONTACT_INFO,
    title    : 'Tell us about the lessee contact',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.lesseeContactFirstName,
      FieldNames.lesseeContactMiddleName,
      FieldNames.lesseeContactLastName,
      FieldNames.lesseeContactSuffixName,
      FieldNames.lesseeContactTitle,
      FieldNames.commercialPhone,
      FieldNames.commercialBusinessEmail,
      FieldNames.lesseeTaxId,
      FieldNames.lesseeBillingStreet,
      FieldNames.lesseeBillingStreet2,
      FieldNames.lesseeBillingCity,
      FieldNames.lesseeBillingState,
      FieldNames.lesseeBillingZip,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_EQUIPMENT_INFO,
    title    : 'Tell us about the equipment vendor',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.vendorName,
      FieldNames.vendorStreet,
      FieldNames.vendorStreet2,
      FieldNames.vendorCity,
      FieldNames.vendorState,
      FieldNames.vendorZip,
      FieldNames.vendorPhone,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_EQUIPMENT_DETAIL_INFO,
    title    : 'Tell us about the equipment',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.commercialEquipmentList,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_TERMS_INFO,
    title    : 'Terms of sale',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.totalEquipmentCost,
      FieldNames.tradeInAmount,
      FieldNames.cashDown,
      FieldNames.rentalCredit,
      FieldNames.amountToFinance,
      FieldNames.paymentFrequency,
      FieldNames.firstPaymentTiming,
      FieldNames.preferredPaymentDate,
      FieldNames.expectedVendorPaymentDate,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_ESSENTIAL_USE_INFO,
    title    : 'Essential use and source of funds',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.equipmentUseDepartment,
      FieldNames.leaseFundSource,
      FieldNames.grantsOrLoansForLease,
      FieldNames.taxExemptObligationsOver10M,
      FieldNames.additionalLeasingAnticipated,
      FieldNames.pastNonAppropriations,
      FieldNames.pastNonAppropriationsExplanation,
      FieldNames.currentDebtRating,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_PRIMARY_BANK_INFO,
    title    : 'Tell us about your primary bank',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.primaryBankName,
      FieldNames.primaryBankStreet,
      FieldNames.primaryBankStreet2,
      FieldNames.primaryBankCity,
      FieldNames.primaryBankState,
      FieldNames.primaryBankZip,
      FieldNames.primaryBankOfficerName,
      FieldNames.primaryBankOfficerPhone,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_FINANCIAL_INFO,
    title    : 'Tell us about your bank or equipment lessor',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.bankOrLessorName,
      FieldNames.bankOrLessorStreet,
      FieldNames.bankOrLessorStreet2,
      FieldNames.bankOrLessorCity,
      FieldNames.bankOrLessorState,
      FieldNames.bankOrLessorZip,
      FieldNames.bankOrLessorPhone,
      FieldNames.bankOrLessorContact,
      FieldNames.bankOrLessorAccountNumber,
    ]),
  },
  {
    id       : COMMERCIAL_LEASE_MUNICIPAL_LAW_FIRM_INFO,
    title    : 'Tell us about your law firm',
    questions: getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.lawFirmName,
      FieldNames.lawFirmStreet,
      FieldNames.lawFirmStreet2,
      FieldNames.lawFirmCity,
      FieldNames.lawFirmState,
      FieldNames.lawFirmZip,
      FieldNames.attorneyName,
      FieldNames.lawFirmPhone,
    ]),
  },
  {
    id           : COMMERCIAL_LEASE_MUNICIPAL_INSURANCE_INFO,
    title        : 'Tell us about your insurance',
    questions    : getCommercialLeaseMunicipalQuestionsByName([
      FieldNames.insuranceCompanyName,
      FieldNames.insuranceCompanyStreet,
      FieldNames.insuranceCompanyStreet2,
      FieldNames.insuranceCompanyCity,
      FieldNames.insuranceCompanyState,
      FieldNames.insuranceCompanyZip,
      FieldNames.insuranceAgentFirstName,
      FieldNames.insuranceAgentMiddleName,
      FieldNames.insuranceAgentLastName,
      FieldNames.insuranceAgentSuffixName,
      FieldNames.insuranceAgentPhone,
      FieldNames.insuranceAgentEmail,
    ]),
  },
  {
    id            : COMMERCIAL_LEASE_MUNICIPAL_SSN,
    ifNoQuestions : CollectLeaseContactSsnStep,
    submitAction  : handleCommercialFormSubmit,
  },
  {
    id              : COMMERCIAL_LEASE_MUNICIPAL_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
